<script>
import Modal from '@/components/atoms/Modal'
import OrderItem from '@/components/atoms/OrderItem'
import OrderDetailsExpenseCodes from '@/components/molecules/OrderDetailsExpenseCodes'
import orderFinance from '@/modules/mixins/orderFinance'
import { mapGetters } from 'vuex'
import sharedTracking from '@/modules/mixins/sharedTracking'
export default {
    name: 'OrderDetails',
    components: { OrderItem, Modal, OrderDetailsExpenseCodes },
    mixins: [orderFinance, sharedTracking],
    props: {
        order: {
            type: Object,
            default: () => {},
        },
        restaurant: {
            type: Object,
            default: () => {},
        },
        orderProcessing: Boolean,
        deliveryFinished: Boolean,
        statusKey: String,
        courierStatusText: String,
    },
    computed: {
        ...mapGetters(['lgDevice']),
        orderFinanceModel() {
            return this.order || {}
        },
        expenseRule() {
            return this.order.selected_expense_rule
        },
        expenseCodes() {
            return this.order.expense_code
        },
        comment() {
            return this.order.comment
        },
    },
    methods: {
        open() {
            this.$refs.orderDetails.openModal()
        },
        closed() {
            this.$emit('closed')
        },
    },
}
</script>

<template lang="pug">
    modal(ref="orderDetails" @clodes="closed" scroll)
        template(v-slot:header)
            .modal-header-text {{$t('profile.order', {id:order.identifier})}}
        template(v-slot:body)
            .d-flex.align-items-center.flex-column.mb-6
                .font-size-sm.text-73 {{$t('order.delivery_from')}}
                .text-26 {{restaurant.name}}
            .d-flex.align-items-center.flex-column.mb-6(v-if="!isSharedTrackingPage")
                .font-size-sm.text-73 {{$t('order.delivered_to')}}
                .text-73 {{order.delivery_full_address }}
            .d-flex.align-items-center.flex-column
                .font-size-sm.text-73 {{$t('order.status')}}
                .text-26
                    template(v-if="deliveryFinished") {{$t('base.delivered')}}
                    template(v-else)
                        template(v-if="orderProcessing")
                            | {{$t('enums.processing')}}
                        template(v-else-if="statusKey")
                            | {{$t(`enums.${statusKey}.title`)}}
                        template(v-else)
                            | {{courierStatusText}}
            .d-flex.align-items-center.flex-column
                hr.stretch(:class="{'w-100': lgDevice}")
                .font-size-sm.text-73 {{$t('order.items_payment')}}
                order-item.w-100(
                    v-for="(item, index) in order.items"
                    :key="`orderedItem-${index}`"
                    :model="item"
                    :locale="order.currency_locale"
                    :currency="order.currency"
                    :noBorder="index === 0"
                    show-price)
                hr.w-100
                .w-100
                    .payment-line(v-if="subTotal")
                        span {{$t('order.subtotal')}}
                        span {{ subTotal | toCurrency(order.currency_locale, order.currency) }}
                        .d-flex.align-items-center.w-100.font-size-sm.text-73.line-1(v-if="restaurantDiscount")
                            icon.text-danger.mr-2(icon="tag" size="sm")
                            | {{ $t('order.discount', {price: `-${$options.filters.toCurrency(restaurantDiscount, order.currency_locale, order.currency)}`}) }}
                    .payment-line(v-if="deliveryPrice >= 0  && originalDeliveryPrice > 0")
                        span {{$t('order.delivery_fee')}}
                        span(:class="{'text-primary': deliveryPriceHighlight}")
                            s.text-73.mr-3(v-if="deliveryPriceHighlight") {{ originalDeliveryPrice | toCurrency(order.currency_locale, order.currency) }}
                            template(v-if="deliveryPrice === 0") {{ $t('order.free') }}
                            template(v-else) {{ deliveryPrice | toCurrency(order.currency_locale, order.currency) }}
                    .payment-line(v-if="promotionUserDiscount > 0")
                        span {{$t('order.promotion_discount')}}
                        span -{{ promotionUserDiscount | toCurrency(order.currency_locale, order.currency) }}
                    .payment-line(v-if="promotionCompanyDiscount > 0")
                        span {{$t('order.promotion_discount')}}
                        span -{{ promotionCompanyDiscount | toCurrency(order.currency_locale, order.currency) }}                    
                    .payment-line(v-if="couponItemDiscount > 0")
                        span {{$t('order.coupon_discount')}}
                        span -{{ couponItemDiscount | toCurrency(order.currency_locale, order.currency) }}
                    .payment-line(v-if="referralDiscount > 0")
                        span {{$t('cart.referral_discount')}}
                        span -{{ referralDiscount | toCurrency(order.currency_locale, order.currency) }}
                    .payment-line(v-if="companyServiceFee > 0")
                        span {{$t('order.company_service_fee')}}
                        span {{ companyServiceFee | toCurrency(order.currency_locale, order.currency) }}
                    .payment-line.mt-5
                        span.font-weight-semibold {{$t('order.total')}}
                        span.font-weight-semibold {{totalPrice | toCurrency(order.currency_locale, order.currency)}}
                    template(v-if="!isSharedTrackingPage")
                      hr
                      span.d-flex.font-weight-semibold.mb-3 {{$t('order.payment_details')}}
                      .payment-line(v-if="expenseRule && paidByCompany > 0")
                          span.text-break {{ $t('order.paid_by_expense_rule', {name: expenseRule.name}) }}
                          span {{ paidByCompany | toCurrency(order.currency_locale, order.currency) }}
                      .payment-line(v-if="paidByUser > 0")
                          span
                              icon.mr-3(:icon="`fa-solid ${order.payment_method === 'cash' ? 'fa-money-bill' : 'fa-credit-card'}`")
                              | {{$t('order.paid_by', {type: $t(`order.payment_methods.${order.payment_method.replaceAll('-', '_')}`).toLowerCase()})}}
                          span {{ sumUser | toCurrency(order.currency_locale, order.currency) }}
                      template(v-if="comment")
                          hr
                          span.d-flex.font-weight-semibold.mb-3 {{$t('details.comment')}}
                          span.text-break {{comment}}
                      template(v-if="expenseCodes")
                          hr
                          span.d-flex.font-weight-semibold.mb-3 {{$t('order.expense_code')}}
                          order-details-expense-codes(
                              v-for="(expenseCodeRow, index) in expenseCodes"
                              :key="`orderExpenseCode-${index}`"
                              :class="{'mt-3': index}"
                              :model="expenseCodeRow")

</template>
